/**
 * One money formatter for the whole app.
 *
 * There were three, and each knew two currencies: `currency === 'USD' ? '$…' :
 * '₹…'`. Everything that was not dollars rendered as rupees, so once regional
 * pricing went in, a Swiss customer was shown "₹99" for a price of CHF 79 —
 * wrong symbol and wrong number in the same string. The backend has always
 * sent the currency alongside the amount; nothing was reading it.
 */

/** Minor units per major unit. 100 nearly everywhere, and the exceptions are
 *  not cosmetic: dividing yen by 100 shows a price a hundredth of the real
 *  one. Mirrors fx_rates.MINOR_UNITS on the backend. */
const MINOR_UNITS: Record<string, number> = {
  JPY: 1, KRW: 1, VND: 1, CLP: 1, ISK: 1,
  BHD: 1000, KWD: 1000, OMR: 1000, JOD: 1000, TND: 1000,
};

export const minorUnits = (currency?: string): number =>
  MINOR_UNITS[(currency || 'INR').toUpperCase()] ?? 100;

/** Minor units (paise, cents, rappen) to the major unit. */
export const toMajor = (minor: number, currency?: string): number =>
  minor / minorUnits(currency);

/**
 * Format an amount given in minor units, in its own currency.
 *
 * Intl carries every symbol and every placement rule — CHF goes before the
 * number, € after it in most European locales — so hardcoding a prefix is
 * wrong for most of the world even when the symbol is right.
 */
export const money = (minor: number | null | undefined, currency = 'INR'): string => {
  if (minor == null || !Number.isFinite(Number(minor))) return '—';
  const cur = (currency || 'INR').toUpperCase();
  const major = toMajor(Number(minor), cur);
  try {
    return new Intl.NumberFormat(cur === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency', currency: cur,
      // Whole prices stay whole: "CHF 79", not "CHF 79.00".
      minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(major);
  } catch {
    // An unrecognised code still shows the number and says what it is in.
    return `${cur} ${major.toLocaleString()}`;
  }
};

/** The bare symbol, for a label that already has the number next to it. */
export const currencySymbol = (currency = 'INR'): string => {
  const cur = (currency || 'INR').toUpperCase();
  try {
    return new Intl.NumberFormat(cur === 'INR' ? 'en-IN' : 'en-US',
      { style: 'currency', currency: cur, maximumFractionDigits: 0 })
      .formatToParts(0).find(p => p.type === 'currency')?.value || cur;
  } catch {
    return cur;
  }
};
